import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useFeature } from "../context/FeatureContext";
import { useReactivate } from "../context/ReactivateContext";
import ReactivatePackageModal from "./ReactivatePackageModal";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../components/ui/tooltip";
import {
  Home, LogOut, User, Thermometer, Building2, MapPin, Users,
  Settings, ChevronDown, Check, Truck, Wrench, BookOpen, Apple,
  CalendarDays, Sparkles, Warehouse, Tag, Wallet, Receipt, MoreHorizontal,
  ArrowLeft, UtensilsCrossed, Package, ShieldCheck, ClipboardList,
  SlidersHorizontal, ChevronRight, UserCog, Clock, Sun, BadgeCheck, Bot, Tags, Boxes,
  ShoppingCart, FileText, BookOpenCheck, FolderArchive, Download, Lock, CreditCard,
  AlertTriangle, Lightbulb, MessageCircle, X, Bell
} from "lucide-react";
import { useState, useEffect, useCallback } from "react";
import { cn } from "../lib/utils";
import axios from "axios";
import FeedbackButton from "./FeedbackButton";
import OnboardingWizard from "./wizard/OnboardingWizard";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Layout = ({ children }) => {
  const { logout, user, token, localeCorrente, locali, cambiaLocale, isAdmin, isCucina, isSala } = useAuth();
  const { hasFeature, getPackageName, trialUsed } = useFeature();
  const { openReactivate } = useReactivate();
  const navigate = useNavigate();
  const location = useLocation();
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);
  const [groupSheetKey, setGroupSheetKey] = useState(null);
  const [localeMenuOpen, setLocaleMenuOpen] = useState(false);
  const [menuBadges, setMenuBadges] = useState({});
  const [availableCompanies, setAvailableCompanies] = useState([]);
  const [openGroups, setOpenGroups] = useState({
    menuRicette: false, acquistiForni: false, haccpOp: false, personale: false, cassaCont: false, archivio: false, impostazioni: false
  });

  const isOperatore = isCucina || isSala;
  useEffect(() => {
    if (!isAdmin || !user?.societa_id) { setAvailableCompanies([]); return; }
    let active = true;
    axios.get(`${API_BASE}/auth/companies`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => { if (active) setAvailableCompanies(res.data.companies || []); })
      .catch(() => { if (active) setAvailableCompanies([]); });
    return () => { active = false; };
  }, [isAdmin, user?.societa_id, token]);
  const handleLogout = () => { logout(); navigate("/login"); };

  // ─── Subscription expiry banner ───
  const [subBanner, setSubBanner] = useState(null); // null | { days_left, scadenza_display, status }
  const [bannerDismissed, setBannerDismissed] = useState(false);

  useEffect(() => {
    if (isOperatore || bannerDismissed) return;
    // Check dismiss key (stored with date so it re-shows after 24h)
    const dismissKey = "sub_banner_dismissed";
    const dismissed = localStorage.getItem(dismissKey);
    if (dismissed) {
      const dismissedAt = parseInt(dismissed, 10);
      if (Date.now() - dismissedAt < 24 * 60 * 60 * 1000) {
        setBannerDismissed(true);
        return;
      }
    }
    const token = localStorage.getItem("token");
    if (!token) return;
    const fetchSub = async () => {
      try {
        const res = await axios.get(`${API_BASE}/payments/subscription-status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const d = res.data;
        if (d.has_subscription && d.days_left != null && (d.is_trial || d.days_left <= 30)) {
          setSubBanner({ days_left: d.days_left, scadenza_display: d.scadenza_display, status: d.status_badge, is_trial: d.is_trial });
        }
      } catch {}
    };
    fetchSub();
  }, [isOperatore, bannerDismissed]);

  const dismissBanner = () => {
    localStorage.setItem("sub_banner_dismissed", Date.now().toString());
    setBannerDismissed(true);
    setSubBanner(null);
  };

  // ─── Wizard state ───
  const [showWizard, setShowWizard] = useState(false);
  const [wizardCompleted, setWizardCompleted] = useState(true);
  const [wizardChecked, setWizardChecked] = useState(false); // evita flash UI durante il fetch iniziale
  const [wizardBannerDismissed, setWizardBannerDismissed] = useState(false);
  const [wizardPreviewMode, setWizardPreviewMode] = useState(false);

  const fetchWizard = async () => {
    const token = localStorage.getItem("token");
    if (!token) { setWizardChecked(true); return; }
    try {
      const res = await axios.get(`${API_BASE}/wizard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const d = res.data;
      if (d.enabled && !d.progress?.completed) {
        setWizardCompleted(false);
        setShowWizard(true);
      } else {
        setWizardCompleted(true);
        setShowWizard(false);
      }
    } catch {} finally {
      setWizardChecked(true);
    }
  };

  // Controlla wizard al mount e quando arriva il param ?wizard_check
  useEffect(() => {
    if (!isAdmin) return;
    fetchWizard();
  }, [isAdmin]); // eslint-disable-line

  // Ri-controlla il wizard dopo un acquisto (navigate("/?wizard_check=1"))
  useEffect(() => {
    if (!isAdmin) return;
    if (window.location.search.includes("wizard_check")) {
      // Piccolo delay per dare tempo al webhook di aggiornare wizard_states
      setTimeout(() => {
        fetchWizard();
        // Rimuovi il param dall'URL senza ricaricare
        window.history.replaceState({}, "", window.location.pathname);
      }, 1500);
    }
  }, [isAdmin, window.location.search]); // eslint-disable-line

  const handleWizardComplete = () => {
    setShowWizard(false);
    setWizardCompleted(true);
    setWizardPreviewMode(false);
  };

  // Fetch notification badges for menu items
  useEffect(() => {
    if (!localeCorrente?.id || isOperatore) return;
    const token = localStorage.getItem("token");
    if (!token) return;
    const headers = { Authorization: `Bearer ${token}` };

    const fetchBadges = async () => {
      const badges = {};
      try {
        const notifRes = await axios.get(`${API_BASE}/dashboard/notifications?locale_id=${localeCorrente.id}`, { headers });
        const n = notifRes.data;
        if (n.temperature?.count > 0) badges["/frigoriferi"] = n.temperature.count;
        if (n.scorte?.count > 0) badges["/operatore/avvisi-scorte"] = n.scorte.count;
        if (n.pulizie_mancate?.count > 0) badges["/programma-pulizie"] = n.pulizie_mancate.count;
        if (n.compiti_mancati?.count > 0) badges["/task-gestione"] = n.compiti_mancati.count;
      } catch (e) { /* ignore */ }
      try {
        const ferieRes = await axios.get(`${API_BASE}/personale/assenze?locale_id=${localeCorrente.id}&stato=pending`, { headers });
        if (ferieRes.data?.length > 0) badges["/personale/ferie"] = ferieRes.data.length;
      } catch (e) { /* ignore */ }
      try {
        const segRes = await axios.get(`${API_BASE}/segnalazioni-locale/conteggi?locale_id=${localeCorrente.id}`, { headers });
        if (segRes.data?.aperte > 0) badges["/operativita/segnalazioni"] = segRes.data.aperte;
      } catch (e) { /* ignore */ }
      try {
        const docRes = await axios.get(`${API_BASE}/documenti/stats/riepilogo`, { headers });
        const docScaduti = (docRes.data?.scaduti || 0) + (docRes.data?.in_scadenza_30g || 0);
        if (docScaduti > 0) badges["/archivio-documenti"] = docScaduti;
      } catch (e) { /* ignore */ }
      setMenuBadges(badges);
    };
    fetchBadges();
    const interval = setInterval(fetchBadges, 60000);
    return () => clearInterval(interval);
  }, [localeCorrente?.id, isOperatore]);

  // Mappa path -> gruppo per determinare quale gruppo aprire in base alla route
  const getGroupForPath = useCallback((pathname) => {
    const pathToGroup = {
      // Menu & Ricette
      "/anagrafica/categorie": "menuRicette",
      "/anagrafica/ricette": "menuRicette",
      "/menu": "menuRicette",
      "/anagrafica/ingredienti": "menuRicette",
      // Acquisti & Fornitori
      "/acquisti/prodotti": "acquistiForni",
      "/acquisti/fornitori": "acquistiForni",
      "/acquisti/ordini": "acquistiForni",
      "/acquisti/food": "acquistiForni",
      "/acquisti/materiali": "acquistiForni",
      "/acquisti/fattura": "acquistiForni",
      "/acquisti/storico": "acquistiForni",
      "/acquisti/import-sdi": "acquistiForni",
      // HACCP & Operatività
      "/frigoriferi": "haccpOp",
      "/anagrafica/elementi-cucina": "haccpOp",
      "/programma-pulizie": "haccpOp",
      "/etichette": "haccpOp",
      "/task-gestione": "haccpOp",
      "/calendario": "haccpOp",
      "/operativita/segnalazioni": "haccpOp",
      // Personale
      "/personale/turni": "personale",
      "/operatore/turni": "personale",
      "/personale/ferie": "personale",
      "/personale/presenze": "personale",
      "/personale/schede": "personale",
      // Cassa & Contabilità
      "/prima-nota": "cassaCont",
      // Archivio & Documenti
      "/archivio-documenti": "archivio",
      // Impostazioni & Anagrafica
      "/anagrafica/utenti": "impostazioni",
      "/anagrafica/locali": "impostazioni",
      // "/anagrafica/societa" removed
      "/anagrafica/tecnici": "impostazioni",
      "/amministrazione/servizi-delivery": "impostazioni",
      "/abbonamento": "impostazioni",
      "/acquisti/config-fatture": "impostazioni",
      "/integrazioni/telegram": "impostazioni",
    };
    
    // Cerca match esatto o match parziale (per route con parametri)
    for (const [path, group] of Object.entries(pathToGroup)) {
      if (pathname === path || pathname.startsWith(path + "/")) {
        return group;
      }
    }
    return null;
  }, []);

  // Sincronizza il gruppo aperto con la route corrente
  useEffect(() => {
    const activeGroup = getGroupForPath(location.pathname);
    if (activeGroup) {
      setOpenGroups(prev => {
        // Se il gruppo è già aperto, non fare nulla
        if (prev[activeGroup]) return prev;
        // Altrimenti, chiudi tutti e apri quello corretto
        const allClosed = Object.keys(prev).reduce((acc, k) => ({ ...acc, [k]: false }), {});
        return { ...allClosed, [activeGroup]: true };
      });
    }
  }, [location.pathname, getGroupForPath]);
  
  // Toggle manuale del gruppo (comportamento accordion)
  const toggleGroup = (key) => {
    setOpenGroups(prev => {
      const isCurrentlyOpen = prev[key];
      // Se il gruppo è già aperto, lo chiudo
      if (isCurrentlyOpen) {
        return { ...prev, [key]: false };
      }
      // Altrimenti, chiudo tutti e apro solo questo
      const allClosed = Object.keys(prev).reduce((acc, k) => ({ ...acc, [k]: false }), {});
      return { ...allClosed, [key]: true };
    });
  };

  // Componente Tooltip Upsell ispirato allo stile Apple frosted glass
  const UpsellTooltipCard = ({ icon: Icon, title, description, iconColor, feature }) => {
    // Phase D: copy diverso per chi ha già usato il trial — incoraggia "riattiva"
    const finalDescription = trialUsed
      ? `Hai usato ${title} durante la prova. Riattiva il pacchetto per continuare ad usarlo.`
      : description;
    const ctaLabel = trialUsed ? "Riattiva pacchetto" : "Scopri il pacchetto";
    return (
    <div style={{ position: "relative", overflow: "hidden", borderRadius: "20px" }}>
      {/* Gradient iridescente di sfondo */}
      <div style={{
        position: "absolute", top: "-30px", right: "-30px", width: "140px", height: "140px",
        background: "radial-gradient(circle, rgba(108,99,255,0.25) 0%, rgba(139,133,255,0.15) 30%, rgba(160,213,242,0.2) 60%, rgba(195,245,218,0.15) 100%)",
        filter: "blur(20px)", pointerEvents: "none", zIndex: 0
      }} />
      <div style={{
        position: "absolute", top: "-20px", left: "-20px", width: "100px", height: "100px",
        background: "radial-gradient(circle, rgba(242,190,234,0.2) 0%, rgba(211,191,240,0.15) 50%, transparent 100%)",
        filter: "blur(18px)", pointerEvents: "none", zIndex: 0
      }} />
      {/* Contenuto */}
      <div style={{ position: "relative", zIndex: 1, padding: "16px" }}>
        {/* Icona in container arrotondato */}
        <div style={{
          width: "36px", height: "36px", borderRadius: "10px", display: "flex",
          alignItems: "center", justifyContent: "center", marginBottom: "12px",
          background: "rgba(108,99,255,0.08)", border: "1px solid rgba(108,99,255,0.1)"
        }}>
          <Icon style={{ width: "18px", height: "18px", color: iconColor || "#6C63FF" }} strokeWidth={1.8} />
        </div>
        {/* Titolo */}
        <p style={{ fontSize: "13px", fontWeight: 700, color: "#1A1A1E", marginBottom: "6px", lineHeight: 1.3 }}>{title}</p>
        {/* Phase D: badge "Già provato" */}
        {trialUsed && (
          <div style={{
            display: "inline-flex", alignItems: "center", gap: "4px",
            padding: "2px 8px", borderRadius: "8px",
            background: "rgba(236,72,153,0.1)", border: "1px solid rgba(236,72,153,0.2)",
            marginBottom: "8px"
          }} data-testid="trial-used-badge">
            <span style={{ fontSize: "10px", fontWeight: 700, color: "#EC4899", letterSpacing: "0.04em" }}>
              GIÀ PROVATO
            </span>
          </div>
        )}
        {/* Descrizione */}
        <p style={{ fontSize: "11.5px", fontWeight: 400, color: "#6B7280", lineHeight: 1.55, marginBottom: "14px" }}>{finalDescription}</p>
        {/* Bottone */}
        {trialUsed ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              openReactivate(title);  // title = nome del pacchetto, es. "HACCP & Operatività"
            }}
            style={{
              display: "flex", alignItems: "center", justifyContent: "center",
              width: "100%", padding: "8px 0", borderRadius: "12px",
              background: "linear-gradient(135deg, #6C63FF, #EC4899)",
              color: "#fff", border: "none", cursor: "pointer",
              fontSize: "12px", fontWeight: 700, textDecoration: "none",
              boxShadow: "0 2px 8px rgba(236,72,153,0.3)",
              transition: "opacity 0.2s"
            }}
            onMouseEnter={(e) => e.currentTarget.style.opacity = "0.9"}
            onMouseLeave={(e) => e.currentTarget.style.opacity = "1"}
            data-testid="upsell-reactivate-btn"
          >
            {ctaLabel}
          </button>
        ) : (
          <NavLink
            to={feature ? `/abbonamento?feature=${encodeURIComponent(feature)}` : "/abbonamento"}
            onClick={(e) => e.stopPropagation()}
            style={{
              display: "flex", alignItems: "center", justifyContent: "center",
              width: "100%", padding: "8px 0", borderRadius: "12px",
              background: "#6C63FF",
              color: "#fff",
              fontSize: "12px", fontWeight: 700, textDecoration: "none",
              boxShadow: "0 2px 8px rgba(108,99,255,0.3)",
              transition: "opacity 0.2s"
            }}
            onMouseEnter={(e) => e.currentTarget.style.opacity = "0.9"}
            onMouseLeave={(e) => e.currentTarget.style.opacity = "1"}
            data-testid="upsell-tooltip-btn"
          >
            {ctaLabel}
          </NavLink>
        )}
      </div>
    </div>
    );
  };

  const navGroups = [
    {
      key: "menuRicette", label: "Menu & Ricette", icon: UtensilsCrossed,
      color: "text-violet-600", textColor: "text-violet-800", bg: "bg-violet-50", activeBg: "bg-violet-500",
      dot: "bg-violet-500",
      items: [
        { to: "/anagrafica/ricette", icon: BookOpen, label: "Ricette", feature: "ricette" },
        { to: "/menu", icon: BookOpenCheck, label: "Menu di vendita", feature: "menu_vendita" },
      ],
    },
    {
      key: "acquistiForni", label: "Acquisti & Fornitori", icon: ShoppingCart,
      color: "text-blue-600", textColor: "text-slate-800", bg: "bg-blue-50", activeBg: "bg-blue-500",
      dot: "bg-blue-500",
      items: [
        { to: "/acquisti/prodotti", icon: Package, label: "Prodotti", feature: "prodotti_catalogo" },
        { to: "/acquisti/fornitori", icon: Truck, label: "Fornitori", feature: "fornitori" },
        { to: "/acquisti/ordini", icon: ShoppingCart, label: "Ordini", feature: "ordini_fornitori" },
        { to: "/acquisti/storico", icon: FileText, label: "Fatture d'acquisto", feature: "fatture_acquisto" },
      ],
    },
    {
      key: "haccpOp", label: "HACCP & Operatività", icon: ShieldCheck,
      color: "text-emerald-500", textColor: "text-emerald-900", bg: "bg-emerald-50", activeBg: "bg-emerald-500",
      dot: "bg-emerald-500",
      items: [
        { to: "/frigoriferi", icon: Thermometer, label: "Controllo temperature", feature: "controllo_temperature" },
        { to: "/programma-pulizie", icon: Sparkles, label: "Piano pulizie", feature: "piano_pulizie" },
        { to: "/etichette", icon: Tag, label: "Tracciabilità lotti", feature: "tracciabilita_lotti" },
        { to: "/task-gestione", icon: ClipboardList, label: "Bacheca compiti", feature: "bacheca_compiti" },
        { to: "/operativita/segnalazioni", icon: AlertTriangle, label: "Segnalazioni", feature: "segnalazioni" },
        { to: "/calendario", icon: CalendarDays, label: "Calendario", feature: "calendario" },
      ],
    },
    {
      key: "personale", label: "Personale", icon: UserCog,
      color: "text-cyan-500", textColor: "text-cyan-900", bg: "bg-cyan-50", activeBg: "bg-cyan-500",
      dot: "bg-cyan-500",
      items: [
        { to: "/personale/turni", icon: CalendarDays, label: "Turni", feature: "turni" },
        { to: "/personale/ferie", icon: Sun, label: "Ferie & Permessi", feature: "ferie_permessi" },
        { to: "/personale/presenze", icon: Clock, label: "Presenze", feature: "presenze" },
        { to: "/personale/schede", icon: BadgeCheck, label: "Schede personale", feature: "schede_personale" },
      ],
    },
    {
      key: "cassaCont", label: "Cassa & Contabilità", icon: Wallet,
      color: "text-pink-500", textColor: "text-pink-900", bg: "bg-pink-50", activeBg: "bg-pink-500",
      dot: "bg-pink-500",
      items: [
        { to: "/prima-nota", icon: Wallet, label: "Cassa & Contabilità", feature: "prima_nota" },
      ],
    },
    {
      key: "archivio", label: "Archivio & Documenti", icon: FolderArchive,
      color: "text-amber-600", textColor: "text-amber-900", bg: "bg-amber-50", activeBg: "bg-amber-500",
      dot: "bg-amber-500",
      items: [
        { to: "/archivio-documenti", icon: FolderArchive, label: "Archivio documenti", feature: "archivio_documenti" },
      ],
    },
    {
      key: "impostazioni", label: "Impostazioni & Anagrafica", icon: SlidersHorizontal,
      color: "text-gray-500", textColor: "text-gray-800", bg: "bg-gray-100", activeBg: "bg-gray-500",
      dot: "bg-gray-500",
      alwaysVisible: true,
      items: [
        { to: "/anagrafica/utenti", icon: Users, label: "Operatori", alwaysOn: true },
        { to: "/anagrafica/locali", icon: MapPin, label: "Locali", alwaysOn: true },
        { to: "/anagrafica/tecnici", icon: Wrench, label: "Contatti utili", feature: "contatti_utili" },
        { to: "/amministrazione/servizi-delivery", icon: Truck, label: "Servizi delivery", feature: "servizi_delivery" },
        { to: "/abbonamento", icon: CreditCard, label: "Il mio abbonamento", alwaysOn: true },
        { to: "/integrazioni/telegram", icon: Bot, label: "Bot Telegram", feature: "bot_telegram" },
      ],
    },
  ];

  // Descrizioni commerciali per gruppi e features bloccate
  const groupDescriptions = {
    menuRicette: "Crea e gestisci ricette, menu di vendita e materie prime. Pubblica i tuoi menu in pochi click e tieni tutto organizzato.",
    acquistiForni: "Fornitori, ordini e fatture sempre sotto controllo. Monitora le spese e ottimizza i costi del tuo locale.",
    haccpOp: "Temperature, pulizie, lotti e compiti: rispetta le normative HACCP senza fogli di carta, tutto in digitale.",
    personale: "Turni, presenze, ferie e schede personale: gestisci il tuo team in modo semplice e automatizzato.",
    cassaCont: "Incassi e uscite con la prima nota digitale. Sempre aggiornata, sempre a portata di mano.",
    archivio: "Archivia e consulta tutti i documenti del tuo locale in modo digitale. Niente più carta, tutto in ordine.",
    impostazioni: "Configura il tuo locale con strumenti avanzati: contatti utili, delivery e integrazioni.",
  };
  const featureDescriptions = {
    contatti_utili: "Salva e consulta i contatti di tecnici e fornitori di fiducia, sempre a portata di mano quando servono.",
    servizi_delivery: "Configura i tuoi servizi di consegna a domicilio e gestisci gli ordini direttamente dalla piattaforma.",
    bot_telegram: "Ricevi notifiche in tempo reale su Telegram: ritardi, approvazioni ferie, incassi e molto altro.",
    ricette: "Crea, modifica e organizza le ricette del tuo locale con procedimenti, costi e foto.",
    menu_vendita: "Componi e pubblica i menu di vendita del tuo locale in pochi click.",
    fornitori: "Tieni sotto controllo l'elenco dei tuoi fornitori con contatti e condizioni.",
    prodotti_catalogo: "Catalogo prodotti food e materiali: prezzi, storico acquisti e storico prezzi.",
    ordini_fornitori: "Gestione ordini ai fornitori e lista prodotti da riordinare.",
    fatture_acquisto: "Consulta lo storico completo delle fatture ricevute dai fornitori.",
    controllo_temperature: "Registra e monitora le temperature dei frigoriferi in conformità HACCP.",
    piano_pulizie: "Pianifica e traccia le attività di pulizia del locale con checklist digitali.",
    tracciabilita_lotti: "Traccia i lotti degli ingredienti con etichette e scadenze automatiche.",
    bacheca_compiti: "Assegna compiti al team e monitora il completamento in tempo reale.",
    segnalazioni: "Ricevi e gestisci segnalazioni dal team con foto e priorità.",
    calendario: "Visualizza turni, scadenze e attività in un calendario unificato.",
    turni: "Crea e gestisci i turni del personale con una vista settimanale intuitiva.",
    ferie_permessi: "Gestisci richieste di ferie e permessi con un workflow di approvazione.",
    presenze: "Monitora entrate e uscite del personale con timbratura e geolocalizzazione.",
    schede_personale: "Consulta le schede di ogni membro del team con documenti e contratti.",
    prima_nota: "Registra incassi e uscite giornaliere con una prima nota digitale completa.",
    archivio_documenti: "Carica e organizza documenti, certificati e contratti del tuo locale.",
  };

  // Calcola stato attivo/bloccato per ogni gruppo (basato su singoli items)
  const getGroupStatus = (group) => {
    if (group.alwaysVisible) return { hasAnyActive: true, allLocked: false };
    const featureItems = group.items.filter(i => i.feature);
    if (featureItems.length === 0) return { hasAnyActive: true, allLocked: false };
    const activeCount = featureItems.filter(i => hasFeature(i.feature)).length;
    return { hasAnyActive: activeCount > 0, allLocked: activeCount === 0 };
  };

  // Ordina: gruppi attivi prima, bloccati dopo
  const sortedNavGroups = [...navGroups].sort((a, b) => {
    const aStatus = getGroupStatus(a);
    const bStatus = getGroupStatus(b);
    if (aStatus.hasAnyActive && !bStatus.hasAnyActive) return -1;
    if (!aStatus.hasAnyActive && bStatus.hasAnyActive) return 1;
    return 0;
  });

  const funzionalitaNavMap = {
    lotti: { to: "/etichette", icon: Tag, label: "Tracciabilità lotti" },
    temperature: { to: "/frigoriferi", icon: Thermometer, label: "Temperature" },
    acquisti: { to: "/acquisti/prodotti", icon: ShoppingCart, label: "Acquisti" },
    fornitori: { to: "/acquisti/fornitori", icon: Truck, label: "Fornitori" },
    pulizie: { to: "/calendario-attivita", icon: CalendarDays, label: "Attività" },
    tecnici: { to: "/anagrafica/tecnici", icon: Wrench, label: "Contatti utili" },
    ricette: { to: "/anagrafica/ricette", icon: BookOpen, label: "Ricette" },
    ingredienti: { to: "/anagrafica/ingredienti", icon: Apple, label: "Materie prime" },
  };

  const mobileBottomAdmin = [
    { to: "/", icon: Home, label: "Home", exact: true },
    { groupKey: "menuRicette", icon: UtensilsCrossed, label: "Ricette", groupMatch: ["/anagrafica/ricette", "/anagrafica/categorie", "/menu"], color: "#6C63FF" },
    { groupKey: "haccpOp", icon: ShieldCheck, label: "HACCP", groupMatch: ["/frigoriferi", "/anagrafica/elementi-cucina", "/programma-pulizie", "/etichette", "/task-gestione", "/calendario", "/operativita/segnalazioni"], color: "#10b981" },
    { groupKey: "personale", icon: UserCog, label: "Personale", groupMatch: ["/personale/turni", "/personale/ferie", "/personale/presenze", "/personale/schede"], color: "#06b6d4" },
    { groupKey: "acquistiForni", icon: ShoppingCart, label: "Acquisti", groupMatch: ["/acquisti/fornitori", "/acquisti/prodotti", "/acquisti/ordini", "/acquisti/storico"], color: "#3b82f6" },
  ];

  const buildOperatoreNavItems = () => {
    const items = [{ to: "/operatore", icon: Home, label: "Home", exact: true, color: "#6C63FF" }];
    (user?.funzionalita || []).forEach(func => {
      if (funzionalitaNavMap[func]) items.push({ ...funzionalitaNavMap[func], color: "#6C63FF" });
    });
    return items;
  };

  const mainNavItems = isAdmin ? mobileBottomAdmin : buildOperatoreNavItems();

  const isGroupActive = (group) => group.items.some(item => location.pathname.startsWith(item.to));

  // Calculate group-level badge as sum of child badges
  const getGroupBadge = (group) => {
    return group.items.reduce((sum, item) => sum + (menuBadges[item.to] || 0), 0);
  };

  // Badge component
  const Badge = ({ count, small }) => {
    if (!count || count <= 0) return null;
    return (
      <span className={`inline-flex items-center justify-center rounded-full font-bold leading-none text-white ${small ? "min-w-[18px] h-[18px] text-[10px] px-1" : "min-w-[20px] h-[20px] text-[11px] px-1.5"}`}
        style={{ background: "#ff3b30", boxShadow: "0 2px 6px rgba(255,59,48,0.35)" }}>
        {count}
      </span>
    );
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#F5F5FA" }}>

      {/* Mobile Header */}
      <header className="md:hidden fixed top-0 left-0 right-0 h-14 bg-card z-50 px-4 flex items-center justify-between" style={{ boxShadow: "0 1px 0 #EEEEF2" }}>
        {isOperatore ? (
          <button onClick={() => navigate("/operatore")} className="flex items-center gap-2 text-[#6C63FF] font-medium text-sm" data-testid="back-to-dashboard-btn">
            <ArrowLeft className="h-5 w-5" />
            Indietro
          </button>
        ) : (
          <button onClick={() => locali.length > 1 && setLocaleMenuOpen(true)} className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-xl flex items-center justify-center" style={{ background: "linear-gradient(135deg, #6C63FF, #FF6584)" }}>
              <span className="text-xs text-white font-bold">{localeCorrente?.nome?.[0] || "R"}</span>
            </div>
            <div className="text-left">
              <p className="text-sm font-semibold text-gray-900 leading-none">{localeCorrente?.nome || "Seleziona"}</p>
            </div>
            {locali.length > 1 && <ChevronDown className="h-4 w-4 text-gray-400" />}
          </button>
        )}
        <div className="flex items-center gap-1">
          {availableCompanies.length > 1 && <button data-testid="mobile-switch-company" title="Cambia società" aria-label="Cambia società" onClick={() => navigate("/scegli-societa")} className="p-2 text-teal-700 hover:bg-teal-50 transition-colors"><Building2 className="h-5 w-5" /></button>}
          <button onClick={() => navigate("/anagrafica/profilo")} className="p-2 rounded-2xl hover:bg-gray-50 transition-colors">
            <div className="w-8 h-8 rounded-full flex items-center justify-center font-semibold text-sm text-white" style={{ background: "linear-gradient(135deg, #6C63FF, #FF6584)" }}>
              {(user?.nome || user?.email || user?.telefono || "?")[0].toUpperCase()}
            </div>
          </button>
          <button onClick={() => setMoreMenuOpen(true)} className="p-2 rounded-2xl hover:bg-gray-50 transition-colors">
            <MoreHorizontal className="h-5 w-5 text-gray-500" />
          </button>
        </div>
      </header>

      {/* Locale Modal */}
      {localeMenuOpen && (
        <div className="fixed inset-0 z-[110]">
          <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={() => setLocaleMenuOpen(false)} />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[90%] max-w-sm bg-card rounded-3xl p-6" style={{ boxShadow: "0 20px 60px rgba(0,0,0,0.15)" }}>
            <h2 className="text-lg font-bold text-foreground mb-4">Seleziona Locale</h2>
            <div className="space-y-2">
              {locali.map(locale => (
                <button key={locale.id} onClick={() => { cambiaLocale(locale); setLocaleMenuOpen(false); }}
                  className={cn("w-full flex items-center justify-between p-4 rounded-2xl transition-all font-medium",
                    localeCorrente?.id === locale.id ? "text-white" : "bg-gray-50 text-gray-700 hover:bg-gray-100")}
                  style={localeCorrente?.id === locale.id ? { background: "linear-gradient(135deg, #6C63FF, #8B85FF)" } : {}}>
                  <span>{locale.nome}</span>
                  {localeCorrente?.id === locale.id && <Check className="h-5 w-5" />}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Desktop Sidebar */}
      <aside className="hidden md:flex md:fixed md:inset-y-0 md:left-0 md:w-64 md:flex-col bg-white" style={{ boxShadow: "1px 0 0 #EEEEF2" }}>
        {/* During wizard preview: the wizard renders its own panel above this sidebar */}
        {/* Logo */}
        <div style={{ padding: '14px 20px', display: 'flex', alignItems: 'center', gap: '12px', borderBottom: '1px solid #f3f4f6' }}>
          <img 
            src="/yollo-logo.png" 
            alt="Yollo" 
            style={{ width: '40px', height: '40px', borderRadius: '50%', flexShrink: 0 }}
          />
          <button onClick={() => locali.length > 1 && setLocaleMenuOpen(true)} style={{ display: 'block', padding: 0, background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left' }}>
            <span style={{ fontSize: '14px', fontWeight: 700, color: '#111827', display: 'block', lineHeight: 1 }}>Yollo</span>
            <span style={{ fontSize: '12px', color: '#6C63FF', fontWeight: 500, display: 'flex', alignItems: 'center', gap: '4px', lineHeight: 1, marginTop: '-2px' }}>
              {localeCorrente?.nome || "Locale"}
              {locali.length > 1 && <ChevronDown style={{ width: '12px', height: '12px', color: '#6C63FF' }} />}
            </span>
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto py-3 px-3 space-y-0.5">
          {/* Dashboard */}
          <NavLink to="/" end
            className={({ isActive }) => cn(
              "flex items-center gap-3 px-3 py-2.5 rounded-2xl text-sm font-medium transition-all mb-1",
              isActive ? "text-white shadow-sm" : "text-gray-500 hover:bg-gray-50 hover:text-gray-800"
            )}
            style={({ isActive }) => isActive ? { background: "linear-gradient(135deg, #6C63FF, #8B85FF)", boxShadow: "0 4px 12px rgba(108,99,255,0.25)" } : {}}>
            <Home className="h-4 w-4" strokeWidth={2} />
            Dashboard
          </NavLink>

          {/* Gruppi */}
          <TooltipProvider delayDuration={200}>
          {sortedNavGroups.map(group => {
            const isActive = isGroupActive(group);
            const isOpen = openGroups[group.key];
            const GroupIcon = group.icon;
            const activeTextColor = group.textColor || group.color;
            const groupStatus = getGroupStatus(group);
            const groupLocked = groupStatus.allLocked;
            return (
              <div key={group.key}>
                {groupLocked ? (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        className="w-full flex items-center gap-3 px-3 py-2.5 rounded-2xl text-sm font-medium text-gray-300 cursor-default"
                        data-testid={`nav-group-${group.key}-locked`}
                      >
                        <GroupIcon className="h-4 w-4 text-gray-300" strokeWidth={1.5} />
                        <span className="flex-1 text-left">{group.label}</span>
                        <Lock className="h-3.5 w-3.5 text-gray-300" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="right" sideOffset={10} data-tooltip-upsell className="max-w-[280px] p-0 border-0">
                      <UpsellTooltipCard
                        icon={GroupIcon}
                        title={group.label}
                        description={groupDescriptions[group.key] || `Sblocca tutte le funzionalità di ${group.label} per il tuo locale.`}
                        iconColor={group.color?.replace("text-", "").includes("[") ? group.color.match(/#[0-9a-fA-F]+/)?.[0] : undefined}
                        feature={group.items?.[0]?.feature}
                      />
                    </TooltipContent>
                  </Tooltip>
                ) : (
                  <button onClick={() => toggleGroup(group.key)}
                    data-testid={`nav-group-${group.key}`}
                    className={cn("w-full flex items-center gap-3 px-3 py-2.5 rounded-2xl text-sm font-medium transition-all",
                      isActive ? cn(group.bg, activeTextColor) : "text-gray-600 hover:bg-gray-50 hover:text-gray-800")}>
                    <GroupIcon className={cn("h-4 w-4", isActive && group.color)} strokeWidth={1.5} />
                    <span className="flex-1 text-left">{group.label}</span>
                    <Badge count={getGroupBadge(group)} small />
                    <ChevronRight className={cn("h-3.5 w-3.5 transition-transform duration-200 opacity-50", isOpen && "rotate-90")} />
                  </button>
                )}
                {groupLocked && (
                  <div className="ml-7 mt-0.5 mb-1">
                    {trialUsed ? (
                      <button
                        type="button"
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); openReactivate(group.label); }}
                        className="text-[10px] font-semibold hover:underline bg-transparent border-0 cursor-pointer p-0"
                        style={{ color: "#EC4899" }}
                        data-testid={`nav-group-${group.key}-reactivate`}
                      >
                        Riattiva pacchetto
                      </button>
                    ) : (
                      <NavLink to={`/abbonamento?feature=${encodeURIComponent(group.items?.[0]?.feature || "")}`} className="text-[10px] font-semibold hover:underline" style={{ color: "#6C63FF" }} data-testid={`nav-group-${group.key}-activate`}>
                        Attiva pacchetto
                      </NavLink>
                    )}
                  </div>
                )}
                {!groupLocked && isOpen && (
                  <div className="ml-4 mt-0.5 mb-0.5 pl-3 border-l-2 space-y-0.5" style={{ borderColor: isActive ? "currentColor" : "#E5E7EB" }}>
                    {group.items.map(item => {
                      const itemLocked = item.feature && !item.alwaysOn && !hasFeature(item.feature);
                      const itemTextColor = group.textColor || group.color;
                      if (itemLocked) {
                        return (
                          <Tooltip key={item.to}>
                            <TooltipTrigger asChild>
                              <div className="flex items-center gap-2.5 px-3 py-2 rounded-xl text-sm text-gray-300 cursor-default" data-testid={`nav-item-locked-${item.feature}`}>
                                <item.icon className="h-3.5 w-3.5 text-gray-300" strokeWidth={1.5} />
                                <span className="flex-1">{item.label}</span>
                                <Lock className="h-3 w-3 text-gray-300" />
                              </div>
                            </TooltipTrigger>
                            <TooltipContent side="right" sideOffset={10} data-tooltip-upsell className="max-w-[280px] p-0 border-0">
                              <UpsellTooltipCard
                                icon={item.icon}
                                title={item.label}
                                description={featureDescriptions[item.feature] || `Questa funzione è inclusa nel pacchetto ${getPackageName(item.feature)}.`}
                                feature={item.feature}
                              />
                            </TooltipContent>
                          </Tooltip>
                        );
                      }
                      return (
                        <NavLink key={item.to} to={item.to}
                          data-testid={`nav-item-${item.to.replace(/\//g, '-').slice(1)}`}
                          className={({ isActive: ia }) => cn(
                            "flex items-center gap-2.5 px-3 py-2 rounded-xl text-sm transition-all",
                            ia ? cn("font-semibold", itemTextColor, group.bg) : "text-gray-500 hover:text-gray-800 hover:bg-gray-50"
                          )}>
                          <item.icon className="h-3.5 w-3.5" strokeWidth={1.5} />
                          <span className="flex-1">{item.label}</span>
                          <Badge count={menuBadges[item.to]} small />
                        </NavLink>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
          </TooltipProvider>
        </nav>

        {/* Footer: profilo + logout */}
        <div className="p-3 border-t border-gray-100 space-y-0.5">
          {availableCompanies.length > 1 && <button type="button" onClick={() => navigate("/scegli-societa")} data-testid="sidebar-switch-company" className="flex items-center gap-3 px-3 py-2 w-full text-sm text-teal-700 hover:bg-teal-50 transition-colors"><Building2 className="h-4 w-4" /> Cambia società</button>}
          <NavLink to="/anagrafica/profilo"
            className={({ isActive }) => cn(
              "flex items-center gap-3 px-3 py-2.5 rounded-2xl mb-1 transition-all",
              isActive ? "bg-gray-100 text-gray-900" : "text-gray-500 hover:bg-gray-50 hover:text-gray-800"
            )}>
            <div className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold text-white flex-shrink-0" style={{ background: "linear-gradient(135deg, #6C63FF, #FF6584)" }}>
              {(user?.nome || user?.email || user?.telefono || "?")[0].toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-gray-900 truncate">{user?.nome || user?.email || user?.telefono}</p>
              <p className="text-xs text-gray-400 capitalize">{user?.ruolo}</p>
            </div>
          </NavLink>
          <button onClick={handleLogout} data-testid="logout-btn"
            className="w-full flex items-center gap-3 px-3 py-2 rounded-2xl text-sm text-gray-400 hover:bg-red-50 hover:text-red-500 transition-colors">
            <LogOut className="h-4 w-4" strokeWidth={1.5} />
            Esci
          </button>
        </div>
      </aside>

      {/* Mobile More Drawer */}
      {moreMenuOpen && (
        <div className="md:hidden fixed inset-0 z-[100]">
          <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={() => setMoreMenuOpen(false)} />
          <div className="absolute bottom-0 left-0 right-0 bg-card rounded-t-[28px] animate-slide-up" style={{ boxShadow: "0 -8px 40px rgba(0,0,0,0.12)" }}>
            <div className="flex justify-center pt-3 pb-2">
              <div className="w-10 h-1 bg-border rounded-full" />
            </div>
            <div className="px-4 pb-8 max-h-[75vh] overflow-y-auto space-y-3">
              {sortedNavGroups.map(group => {
                const GroupIcon = group.icon;
                const groupStatus = getGroupStatus(group);
                const groupLocked = groupStatus.allLocked;
                return (
                  <div key={group.key}>
                    <div className={cn("flex items-center gap-2 px-3 py-2 rounded-2xl mb-1", groupLocked ? "bg-gray-100" : group.bg)}>
                      <GroupIcon className={cn("h-4 w-4", groupLocked ? "text-gray-300" : group.color)} />
                      <p className={cn("text-xs font-bold uppercase tracking-wider flex-1", groupLocked ? "text-gray-300" : group.color)}>{group.label}</p>
                      {!groupLocked && <Badge count={getGroupBadge(group)} small />}
                      {groupLocked && <Lock className="h-3 w-3 text-gray-300" />}
                    </div>
                    {groupLocked ? (
                      <div className="px-4 py-1.5">
                        {trialUsed ? (
                          <button
                            type="button"
                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); setMoreMenuOpen(false); openReactivate(group.label); }}
                            className="text-[11px] font-semibold hover:underline bg-transparent border-0 cursor-pointer p-0"
                            style={{ color: "#EC4899" }}
                          >
                            Riattiva pacchetto
                          </button>
                        ) : (
                          <NavLink to="/abbonamento" onClick={() => setMoreMenuOpen(false)} className="text-[11px] font-semibold hover:underline" style={{ color: "#6C63FF" }}>
                            Attiva pacchetto
                          </NavLink>
                        )}
                      </div>
                    ) : (
                    <div className="space-y-0.5">
                      {group.items.map(item => {
                        const itemLocked = item.feature && !item.alwaysOn && !hasFeature(item.feature);
                        if (itemLocked) {
                          return (
                            <div key={item.to} className="flex items-center gap-3 px-4 py-2.5 rounded-2xl text-gray-300">
                              <item.icon className="h-4 w-4 text-gray-300" strokeWidth={1.5} />
                              <span className="text-sm font-medium flex-1">{item.label}</span>
                              <Lock className="h-3 w-3 text-gray-300" />
                            </div>
                          );
                        }
                        return (
                          <NavLink key={item.to} to={item.to} onClick={() => setMoreMenuOpen(false)}
                            className={({ isActive }) => cn(
                              "flex items-center gap-3 px-4 py-2.5 rounded-2xl transition-all",
                              isActive ? cn("font-semibold", group.color, group.bg) : "text-gray-600 hover:bg-gray-50"
                            )}>
                            <item.icon className="h-4 w-4" strokeWidth={1.5} />
                            <span className="text-sm font-medium flex-1">{item.label}</span>
                            <Badge count={menuBadges[item.to]} small />
                          </NavLink>
                        );
                      })}
                    </div>
                    )}
                  </div>
                );
              })}

              {/* Profilo + Logout */}
              <div className="border-t border-gray-100 pt-3 space-y-1">
                <NavLink to="/anagrafica/profilo" onClick={() => setMoreMenuOpen(false)}
                  className="flex items-center gap-3 px-4 py-3 rounded-2xl hover:bg-gray-50 transition-colors">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold text-white flex-shrink-0" style={{ background: "linear-gradient(135deg, #6C63FF, #FF6584)" }}>
                    {(user?.nome || user?.email || user?.telefono || "?")[0].toUpperCase()}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">{user?.nome || user?.email || user?.telefono}</p>
                    <p className="text-xs text-gray-400 capitalize">{user?.ruolo} · Il mio Profilo</p>
                  </div>
                </NavLink>
                <button onClick={() => { handleLogout(); setMoreMenuOpen(false); }}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-2xl hover:bg-red-50 transition-colors">
                  <div className="w-10 h-10 bg-red-50 rounded-full flex items-center justify-center">
                    <LogOut className="h-4 w-4 text-red-400" />
                  </div>
                  <span className="text-sm font-semibold text-red-400">Esci</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Mobile Group Submenu (2° livello del footer admin) */}
      {groupSheetKey && (() => {
        const group = navGroups.find(g => g.key === groupSheetKey);
        if (!group) return null;
        const GroupIcon = group.icon;
        const groupStatus = getGroupStatus(group);
        const groupLocked = groupStatus.allLocked;
        return (
          <div className="md:hidden fixed inset-0 z-[100]" data-testid="footer-group-sheet">
            <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={() => setGroupSheetKey(null)} />
            <div className="absolute bottom-0 left-0 right-0 bg-card rounded-t-[28px] animate-slide-up" style={{ boxShadow: "0 -8px 40px rgba(0,0,0,0.12)" }}>
              <div className="flex justify-center pt-3 pb-2">
                <div className="w-10 h-1 bg-border rounded-full" />
              </div>
              <div className="px-4 pb-8 pb-safe max-h-[70vh] overflow-y-auto">
                <div className={cn("flex items-center gap-2 px-3 py-2 rounded-2xl mb-2", groupLocked ? "bg-gray-100" : group.bg)}>
                  <GroupIcon className={cn("h-4 w-4", groupLocked ? "text-gray-300" : group.color)} />
                  <p className={cn("text-xs font-bold uppercase tracking-wider flex-1", groupLocked ? "text-gray-300" : group.color)}>{group.label}</p>
                  {!groupLocked && <Badge count={getGroupBadge(group)} small />}
                  {groupLocked && <Lock className="h-3 w-3 text-gray-300" />}
                </div>
                {groupLocked ? (
                  <div className="px-4 py-2">
                    {trialUsed ? (
                      <button type="button"
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setGroupSheetKey(null); openReactivate(group.label); }}
                        className="text-sm font-semibold hover:underline bg-transparent border-0 cursor-pointer p-0"
                        style={{ color: "#EC4899" }}>
                        Riattiva pacchetto
                      </button>
                    ) : (
                      <NavLink to="/abbonamento" onClick={() => setGroupSheetKey(null)} className="text-sm font-semibold hover:underline" style={{ color: "#6C63FF" }}>
                        Attiva pacchetto
                      </NavLink>
                    )}
                  </div>
                ) : (
                  <div className="space-y-0.5">
                    {group.items.map(item => {
                      const itemLocked = item.feature && !item.alwaysOn && !hasFeature(item.feature);
                      if (itemLocked) {
                        return (
                          <div key={item.to} className="flex items-center gap-3 px-4 py-3 rounded-2xl text-gray-300">
                            <item.icon className="h-5 w-5 text-gray-300" strokeWidth={1.5} />
                            <span className="text-sm font-medium flex-1">{item.label}</span>
                            <Lock className="h-3 w-3 text-gray-300" />
                          </div>
                        );
                      }
                      return (
                        <NavLink key={item.to} to={item.to} onClick={() => setGroupSheetKey(null)}
                          data-testid={`footer-group-item-${item.to}`}
                          className={({ isActive }) => cn(
                            "flex items-center gap-3 px-4 py-3 rounded-2xl transition-all",
                            isActive ? cn("font-semibold", group.color, group.bg) : "text-gray-600 hover:bg-gray-50"
                          )}>
                          <item.icon className="h-5 w-5" strokeWidth={1.5} />
                          <span className="text-sm font-medium flex-1">{item.label}</span>
                          <Badge count={menuBadges[item.to]} small />
                        </NavLink>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })()}


      {/* Mobile Bottom Nav */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-card z-50" style={{ boxShadow: "0 -1px 0 #EEEEF2" }}>
        {wizardPreviewMode && (
          <div className="absolute inset-0 z-50" style={{ background: "rgba(255,255,255,0.9)", backdropFilter: "blur(2px)" }} />
        )}
        <div className="flex h-[4.5rem] items-stretch justify-around px-2 pb-safe">
          {mainNavItems.map((item) => {
            const Icon = item.icon;
            const isActive = item.exact
              ? location.pathname === item.to
              : item.groupMatch
                ? item.groupMatch.some(p => location.pathname.startsWith(p))
                : location.pathname.startsWith(item.to);
            const color = item.color || "#6C63FF";
            const iconEl = (
              <>
                <div className={cn("w-10 h-10 flex items-center justify-center rounded-full transition-all", isActive ? "" : "bg-transparent")}
                  style={isActive ? { backgroundColor: `${color}18` } : {}}>
                  <Icon className="h-5 w-5 transition-colors" strokeWidth={isActive ? 2.2 : 1.5}
                    style={{ color: isActive ? color : "#9CA3AF" }} />
                </div>
                <span className="text-[10px] font-semibold transition-colors" style={{ color: isActive ? color : "#9CA3AF" }}>
                  {item.label}
                </span>
              </>
            );
            if (item.groupKey) {
              return (
                <button key={item.groupKey} data-testid={`footer-group-${item.groupKey}`}
                  onClick={() => setGroupSheetKey(item.groupKey)}
                  className="flex flex-col items-center justify-center gap-1 flex-1 active:scale-95 transition-transform">
                  {iconEl}
                </button>
              );
            }
            return (
              <NavLink key={item.to} to={item.to} data-testid={`footer-link-${item.label.toLowerCase()}`}
                className="flex flex-col items-center justify-center gap-1 flex-1 active:scale-95 transition-transform">
                {iconEl}
              </NavLink>
            );
          })}
          <button onClick={() => setMoreMenuOpen(true)}
            className="flex flex-col items-center justify-center gap-1 flex-1 active:scale-95 transition-transform">
            <div className="w-10 h-10 flex items-center justify-center rounded-full">
              <MoreHorizontal className="h-5 w-5 text-gray-400" strokeWidth={1.5} />
            </div>
            <span className="text-[10px] font-semibold text-gray-400">Altro</span>
          </button>
        </div>
      </nav>

      {/* Main Content */}
      <main className={`md:ml-64 pt-14 md:pt-0 pb-24 md:pb-0 min-h-screen ${wizardPreviewMode ? "pb-32 md:pb-24" : ""}`}>
        {/* Wizard banner — shown when wizard is enabled but not completed and user has dismissed wizard */}
        {!wizardCompleted && !showWizard && !wizardBannerDismissed && (
          <div
            className="flex items-center gap-3 px-4 py-2.5"
            style={{ background: "#6C63FF" }}
            data-testid="wizard-resume-banner"
          >
            <Settings className="h-4 w-4 flex-shrink-0" style={{ color: "#fff" }} />
            <span className="flex-1 text-sm font-medium" style={{ color: "#fff" }}>
              Completa la configurazione del tuo locale
            </span>
            <button
              onClick={() => setShowWizard(true)}
              data-testid="wizard-banner-riprendi"
              className="px-3 py-1 rounded-lg text-xs font-bold transition-colors whitespace-nowrap"
              style={{ background: "#fff", color: "#6C63FF" }}
            >
              Riprendi →
            </button>
          </div>
        )}

        {/* Subscription expiry banner */}
        {subBanner && !bannerDismissed && location.pathname !== "/abbonamento" && (
          subBanner.is_trial ? (
            // ── Phase A: Trial sticky countdown banner — conversion-focused ──
            <div
              className="relative overflow-hidden flex items-center gap-3 px-4 py-2.5 text-sm font-medium"
              style={{
                background: subBanner.days_left <= 1
                  ? "linear-gradient(135deg, #F97316, #EF4444)"
                  : subBanner.days_left <= 3
                  ? "linear-gradient(135deg, #F59E0B, #F97316)"
                  : "linear-gradient(135deg, #6C63FF, #8B5CF6, #EC4899)",
                color: "#fff",
              }}
              data-testid="trial-countdown-banner"
            >
              {/* Decorative shimmer */}
              <div
                className="absolute inset-0 pointer-events-none opacity-20"
                style={{
                  background: "radial-gradient(circle at 20% 50%, rgba(255,255,255,0.4), transparent 50%)",
                }}
              />
              <span className="relative z-10 text-base" aria-hidden="true">🎁</span>
              <span className="relative z-10 flex-1 leading-tight">
                {subBanner.days_left <= 0 ? (
                  <strong>La prova è terminata oggi — scegli il tuo piano per continuare.</strong>
                ) : subBanner.days_left === 1 ? (
                  <><strong>Ultimo giorno di prova!</strong> Scegli ora il tuo piano per non perdere l'accesso.</>
                ) : (
                  <>Hai ancora <strong>{subBanner.days_left} giorn{subBanner.days_left === 1 ? "o" : "i"} di prova gratuita</strong> — esplora tutti i pacchetti e trova quello giusto per te.</>
                )}
              </span>
              <NavLink
                to="/abbonamento"
                className="relative z-10 px-3.5 py-1.5 rounded-full text-xs font-bold whitespace-nowrap bg-white hover:scale-[1.03] transition-transform shadow-sm"
                style={{ color: subBanner.days_left <= 3 ? "#DC2626" : "#6C63FF" }}
                data-testid="trial-banner-cta"
              >
                Scopri i pacchetti →
              </NavLink>
              <button
                onClick={dismissBanner}
                className="relative z-10 p-1 rounded-lg hover:bg-white/15 transition-colors flex-shrink-0"
                style={{ color: "#fff" }}
                data-testid="sub-banner-dismiss-btn"
                aria-label="Chiudi"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <div
              className={`flex items-center gap-3 px-4 py-3 text-sm font-medium ${
                subBanner.status === "scaduto" || subBanner.days_left < 0
                  ? "bg-red-600"
                  : subBanner.status === "urgente"
                  ? "bg-orange-500"
                  : "bg-amber-400"
              }`}
              style={{ color: subBanner.status === "scaduto" || subBanner.days_left < 0 || subBanner.status === "urgente" ? "#ffffff" : undefined }}
              data-testid="sub-expiry-banner"
            >
              <Bell className="h-4 w-4 flex-shrink-0" style={{ color: "inherit" }} />
              <span className="flex-1" style={{ color: "inherit" }}>
                {subBanner.days_left < 0
                  ? "Il tuo abbonamento è scaduto. Rinnova ora per continuare ad usare Yollo."
                  : subBanner.days_left === 0
                  ? "Il tuo abbonamento scade oggi!"
                  : `Il tuo abbonamento scade tra ${subBanner.days_left} giorni (${subBanner.scadenza_display}).`}
              </span>
              <NavLink
                to="/abbonamento"
                className={`px-3 py-1 rounded-lg text-xs font-bold whitespace-nowrap transition-opacity hover:opacity-80 ${
                  subBanner.status === "scaduto" || subBanner.days_left < 0 || subBanner.status === "urgente"
                    ? "bg-white text-red-600"
                    : "bg-amber-950/15 text-amber-950"
                }`}
                data-testid="sub-banner-renew-btn"
              >
                Rinnova ora
              </NavLink>
              <button
                onClick={dismissBanner}
                className="p-1 rounded-lg hover:opacity-70 transition-opacity flex-shrink-0"
                style={{ color: "inherit" }}
                data-testid="sub-banner-dismiss-btn"
                aria-label="Chiudi"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          )
        )}
        <div className="p-4 md:p-8 max-w-7xl mx-auto">
          {children}
        </div>
      </main>

      <FeedbackButton />

      {/* Onboarding Wizard — non mostrare nelle pagine self-service di gestione abbonamento/pagamenti.
          Mostrato solo dopo che fetchWizard ha confermato che è davvero da aprire (no flash). */}
      {wizardChecked && showWizard && !["/billing-overview", "/abbonamento"].includes(location.pathname) && (
        <OnboardingWizard
          onComplete={handleWizardComplete}
          onPreviewModeChange={setWizardPreviewMode}
        />
      )}

      <style>{`
        .pb-safe { padding-bottom: max(0.5rem, env(safe-area-inset-bottom)); }
        @keyframes slide-up { from { transform: translateY(100%); } to { transform: translateY(0); } }
        .animate-slide-up { animation: slide-up 0.28s cubic-bezier(0.32,0.72,0,1); }
        #emergent-badge, [id*="emergent"], a[href*="emergent.sh"], [class*="emergent"], div[style*="position: fixed"][style*="bottom"] {
          display: none !important;
          visibility: hidden !important;
          opacity: 0 !important;
          pointer-events: none !important;
        }
      `}</style>
      <ReactivatePackageModal />
    </div>
  );
};

export default Layout;
